import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, type LucideIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'
import type { FollowUpItem, FollowUpLists, FollowUpView } from '@/types'
import { FOLLOW_UP_VIEWS, quietLabel } from './followUpViews'

/**
 * One of the reps' dashboard follow-up cards (Accounts, Leads, Opportunities). Three views,
 * all using the live rules in backend/src/services/dealActivity.js, so the numbers match the
 * Exec Sales Dashboard and the list each "View all" opens.
 */
export function FollowUpCard({
  title,
  noun,
  icon: Icon,
  lists,
  isLoading,
  isError,
  itemHref,
  viewAllHref,
}: {
  title: string
  /** Plural, lower case: "accounts", "leads", "opportunities". */
  noun: string
  icon: LucideIcon
  lists?: FollowUpLists
  isLoading: boolean
  isError?: boolean
  itemHref: (item: FollowUpItem) => string
  viewAllHref: (view: FollowUpView) => string
}) {
  const [view, setView] = useState<FollowUpView>('stale_no_next')
  const current = lists?.[view]
  const meta = FOLLOW_UP_VIEWS.find((v) => v.key === view)!
  const listId = `followup-${noun}-${view}`

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          {title}
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 py-0"
            style={
              view === 'stale'
                ? {
                    backgroundColor: GRACKLE_COLORS.pollen,
                    color: GRACKLE_COLORS.prussianBlue,
                    borderColor: GRACKLE_COLORS.pollen,
                  }
                : {
                    backgroundColor: CATEGORICAL.raspberry.solid,
                    color: '#FFFFFF',
                    borderColor: CATEGORICAL.raspberry.solid,
                  }
            }
            aria-label={`${current?.count ?? 0} ${noun} ${meta.label.toLowerCase()}`}
          >
            {current?.count ?? 0}
          </Badge>
        </CardTitle>
        <div
          className="flex flex-wrap gap-1 mt-1"
          role="group"
          aria-label={`${title} follow-up view`}
        >
          {FOLLOW_UP_VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => setView(v.key)}
              className={`text-[11px] px-2 py-0.5 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                view === v.key
                  ? 'bg-primary text-white'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
              aria-pressed={view === v.key}
              aria-controls={listId}
              title={v.hint}
            >
              {v.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-2">{meta.hint}</p>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : isError ? (
          <p className="text-sm text-destructive">
            This list couldn&apos;t be loaded. Reload the page to try again.
          </p>
        ) : current && current.items.length > 0 ? (
          <>
            <ul
              id={listId}
              className="space-y-1 max-h-48 overflow-y-auto"
              aria-label={`${title}: ${meta.label}`}
            >
              {current.items.map((item) => (
                <li key={item.id} className="flex items-baseline justify-between gap-2">
                  <Link
                    to={itemHref(item)}
                    className="text-sm text-primary underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded truncate"
                  >
                    {item.name || 'Unnamed'}
                  </Link>
                  <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
                    {quietLabel(item)}
                  </span>
                </li>
              ))}
            </ul>
            <Link
              to={viewAllHref(view)}
              className="mt-2 inline-block text-xs font-medium text-primary underline focus:outline-none focus:ring-2 focus:ring-primary rounded"
            >
              View all {current.count} {noun}
              <span className="sr-only">: {meta.label.toLowerCase()}</span>
            </Link>
          </>
        ) : (
          <div id={listId} className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
            {meta.empty(noun)}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
